import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ERR, ago, tone, utc } from "./syncText";

export function Chip({ status, testId }) {
  return (
    <span data-testid={testId} className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tone(status)}`}>
      {String(status || "none").replace(/_/g, " ")}
    </span>
  );
}

function Details({ ep, snap }) {
  return (
    <div className="mt-2 space-y-0.5 text-[10px] text-slate-500 font-mono break-all">
      <div>Route: {ep?.route || snap?.route || "—"}</div>
      <div>Schema check: {snap ? (snap.schema_ok ? "expected fields present" : `missing ${Object.keys(snap.missing_required || {}).join(", ") || "—"}`) : "—"}</div>
      {snap?.unknown_fields?.length > 0 && <div>Extra broker fields: {snap.unknown_fields.slice(0, 8).join(", ")}</div>}
      <div>Content SHA-256: {snap?.content_sha256 ? `${snap.content_sha256.slice(0, 16)}…` : "—"}</div>
      {snap?.unchanged_since_run && <div>Unchanged since run {snap.unchanged_since_run}</div>}
      {(snap?.empty_form || ep?.empty_form) && <div data-testid="sync-empty-form">Broker reported an empty result ({snap?.empty_form || ep?.empty_form})</div>}
      {ep?.shape && (
        <div data-testid="sync-shape">Response structure: {ep.shape.payload_type}{ep.shape.envelope_keys ? ` · envelope [${ep.shape.envelope_keys.join(", ")}]` : ""}{ep.shape.data_type ? ` · data ${ep.shape.data_type}` : ""}{ep.shape.data_keys ? ` [${ep.shape.data_keys.join(", ")}]` : ""}{ep.shape.status_class ? ` · status ${ep.shape.status_class}` : ""}{ep.shape.message_class ? ` · message ${ep.shape.message_class}` : ""}{ep.shape.data_string_class ? ` · data-string ${ep.shape.data_string_class} (${ep.shape.data_len_bucket} chars)` : ""}</div>
      )}
    </div>
  );
}

export function EndpointCard({ kind, label, ep, snap }) {
  const [open, setOpen] = useState(false);
  const status = ep?.status || snap?.status;
  const warn = ep && !["ok", "unverified"].includes(ep.status);
  return (
    <div data-testid={`sync-card-${kind}`} className={`rounded-md border p-3 bg-[var(--surface)] ${warn ? "border-amber-500/40" : "border-[var(--border-subtle)]"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-200">{label}</span>
        <Chip status={status} testId={`sync-card-${kind}-status`} />
      </div>
      {kind === "funds" && (
        <div data-testid="sync-funds-unverified" className="mt-1 text-[10px] text-sky-300">Data received ≠ financially verified — every funds field meaning is UNKNOWN until you approve it with evidence</div>
      )}
      {kind === "positions" && (
        <div data-testid="sync-positions-unverified" className="mt-1 text-[10px] text-sky-300">UNVERIFIED — endpoint and schema not yet validated on the real account</div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
        <span>Items</span><span data-testid={`sync-card-${kind}-items`} className="font-mono text-right">{snap ? snap.item_count : "—"}</span>
        <span>Rejected rows</span><span className={`font-mono text-right ${snap?.rejected_count ? "text-amber-300" : ""}`}>{snap ? snap.rejected_count : "—"}</span>
        <span>Freshness</span><span data-testid={`sync-card-${kind}-fresh`} className="font-mono text-right">{snap ? `${snap.freshness.state} · ${ago(snap.freshness.age_seconds)}` : "no snapshot"}</span>
        <span>Broker time</span><span className="font-mono text-right truncate">{snap?.broker_ts || "—"}</span>
        <span>Captured</span><span className="font-mono text-right">{utc(snap?.captured_at)}</span>
      </div>
      {ep?.error && <div data-testid={`sync-card-${kind}-error`} className="mt-2 text-[11px] text-amber-300">{ERR[ep.error] || ep.error}</div>}
      <button data-testid={`sync-card-${kind}-toggle`} onClick={() => setOpen(!open)} className="mt-2 flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Operational details
      </button>
      {open && <Details ep={ep} snap={snap} />}
    </div>
  );
}
